import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  CryptoFormatter,
  IntlManager,
  NotificationManager,
  SiteConfigManager,
  useSettings,
} from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, Input, Label, RadioSelect, Select } from '@atlas/ui';

import PaymentMethodItem from 'components/ClientProfile/PaymentTab/PaymentMethodItem';

import { submitWithdrawal } from '../withdrawalUtils';

import { WithdrawalBitcoinPopupInfoLabel } from './WithdrawalBitcoinPopupInfoLabel';

const optionsRadioSelect = [{ value: true }, { value: false }];

export const WithdrawalBitcoinPopupV2 = ({
  data: { addressKey = 'cryptoAddress', paymentMethod, redirectUrl, withTag, withoutInfo },
  id,
}) => {
  const { lang } = useSettings();
  const languageCode = useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);
  const [isActiveAddressInput, setActiveAddressInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavedAddress, setIsSavedAddress] = useState(true);
  const [savedAddress, setSavedAddress] = useState();
  const [newAddress, setNewAddress] = useState();
  const [tag, setTag] = useState();
  const {
    addresses,
    amount,
    currencyFrom,
    currencyTo,
    exchangeAmount,
    exchangeRate,
    extTxId,
    feeAmount,
    feeCurrency,
    paymentId,
    pmName,
  } = parseWithdrawalQuery(redirectUrl) || {};
  const { id: pmId } = paymentMethod;

  const address = useMemo(
    () => (addresses && isSavedAddress ? savedAddress : newAddress),
    [addresses, isSavedAddress, savedAddress, newAddress],
  );
  const inputLabel = useMemo(() => (addresses ? 'new_address' : 'address'), [addresses]);
  const optionsSelect = useMemo(() => {
    const addressesArray = Array.isArray(addresses) ? addresses : [addresses];
    return addressesArray?.map(item => ({ text: item, value: item }));
  }, [addresses]);

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);
  const handleChangeAddresses = useCallback(option => setIsSavedAddress(option.value), []);
  const handleChangeInput = useCallback(data => setNewAddress(data.target.value.trim()), []);
  const handleChangeTagInput = useCallback(data => setTag(data.target.value.trim()), []);

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const additionalParameters = {
        [addressKey]: address,
        cryptoCurrency: currencyTo,
        fiatCurrency: currencyFrom,
        pmName,
        publicPaymentId: extTxId || paymentId,
      };

      if (withTag) {
        additionalParameters.cryptoTag = tag;
      }

      const requestData = { additionalParameters, amount, currency: currencyFrom, pmId };
      await submitWithdrawal({ amount, currency: currencyFrom, paymentMethod, requestData });
    } catch {
      NotificationManager.notify({
        message: 'withdrawal_error_message',
        status: 'error',
        type: 'notification',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    addressKey,
    address,
    currencyTo,
    currencyFrom,
    pmName,
    extTxId,
    paymentId,
    withTag,
    amount,
    pmId,
    tag,
    paymentMethod,
    handleClose,
  ]);

  useEffect(
    () => () => {
      setIsSavedAddress(true);
      setSavedAddress(null);
      setNewAddress(null);
    },
    [],
  );

  const currencyToFormatter = useMemo(
    () => new CryptoFormatter(languageCode, currencyTo),
    [currencyTo, languageCode],
  );

  const feeFormatter = useMemo(
    () => new CryptoFormatter(languageCode, feeCurrency),
    [feeCurrency, languageCode],
  );

  return (
    <DefaultPopup headerTitle="withdrawal" headerTitlePosition="left" onClose={handleClose}>
      <div className="withdrawal-bitcoin__body">
        <div className="withdrawal-bitcoin__payment-method">
          <PaymentMethodItem paymentMethod={paymentMethod} />
        </div>

        {addresses && (
          <>
            <Label message="select_address" />:
          </>
        )}
        <div
          className={cx('withdrawal-bitcoin__from-wrapper', {
            'with-tag': withTag,
            'without-info': withoutInfo,
          })}
        >
          {addresses && (
            <RadioSelect
              className="withdrawal-bitcoin__radio"
              onChange={handleChangeAddresses}
              options={optionsRadioSelect}
              value={isSavedAddress}
            />
          )}

          <div className="withdrawal-bitcoin__addresses">
            {addresses && (
              <Select
                className="withdrawal-bitcoin__select"
                disabled={addresses && !isSavedAddress}
                label="saved_address"
                onChange={setSavedAddress}
                options={optionsSelect}
              />
            )}

            <Input
              active={isActiveAddressInput === 'address'}
              className="withdrawal-bitcoin__input"
              disabled={addresses && isSavedAddress}
              label={inputLabel}
              onBlur={() => setActiveAddressInput('')}
              onChange={handleChangeInput}
              onFocus={() => setActiveAddressInput('address')}
              value={newAddress}
            />
          </div>

          {withTag && (
            <div className="withdrawal-bitcoin__tag">
              <Input
                className="withdrawal-bitcoin__input"
                disabled={isSubmitting}
                label="crypto_tag"
                onChange={handleChangeTagInput}
                value={tag}
              />
            </div>
          )}
        </div>

        {!withoutInfo && (
          <div className="withdrawal-bitcoin__info">
            <div>
              <WithdrawalBitcoinPopupInfoLabel label="withdrawal_rate" />
              {`${currencyToFormatter.format(10 ** 9)} = ${IntlManager.formatMoney(exchangeRate * 100)}`}
            </div>

            <div>
              <WithdrawalBitcoinPopupInfoLabel label="amount" />
              {`${IntlManager.formatMoney(amount)} (≈ ${currencyToFormatter.format(exchangeAmount * 10 ** 9)})`}
            </div>

            {Boolean(feeAmount) && !Number.isNaN(feeAmount) && (
              <div>
                <WithdrawalBitcoinPopupInfoLabel label="fee" />
                {`≈ ${feeFormatter.format(feeAmount * 10 ** 9)}`}
              </div>
            )}

            <div className="withdrawal-bitcoin__total">
              <WithdrawalBitcoinPopupInfoLabel label="total_amount" />
              {`≈ ${currencyToFormatter.format((exchangeAmount - feeAmount) * 10 ** 9)}`}
            </div>
          </div>
        )}

        <Button
          className="withdrawal-bitcoin__button"
          disabled={!address || isSubmitting}
          label="withdrawal"
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </DefaultPopup>
  );
};

WithdrawalBitcoinPopupV2.propTypes = {
  data: PropTypes.shape({
    addressKey: PropTypes.string,
    paymentMethod: PropTypes.string,
    redirectUrl: PropTypes.string,
    withTag: PropTypes.string,
    withoutInfo: PropTypes.bool,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
